"use client";

import type { JSX, ReactNode } from "react";
import { Icon, Text, View } from "@/components";
import { useLayout } from "@/providers";
import type { AttemptQuestion, AttemptResult } from "@/types/elearning";

export interface ResultCardProps {
  /** Resultado corregido por el servidor (conteo simple vs. min_correctas). */
  result: AttemptResult;
  /**
   * Preguntas del intento (si siguen en memoria) para mostrar el enunciado en la
   * revisión. Si la página se recargó, la revisión degrada a numeración.
   */
  questions?: AttemptQuestion[];
  /** CTA de cierre (volver al curso, ver certificado, reintentar). */
  children?: ReactNode;
}

/**
 * @name ResultCard
 * @description Resultado de la evaluación (A7). Para el quiz de módulo muestra la
 * corrección por pregunta con su explicación (feedback inmediato); para el examen
 * final el backend no envía revisión (feedback diferido) y se muestran solo el
 * puntaje y los temas a reforzar. La corrección la hace siempre el servidor.
 *
 * @param {ResultCardProps} props - Resultado, preguntas en memoria y CTA.
 * @returns {JSX.Element} La tarjeta de resultado.
 */
const ResultCard = ({
  result,
  questions = [],
  children
}: ResultCardProps): JSX.Element => {
  const { t } = useLayout();
  const passed = !!result.passed;
  const enunciadoBy = new Map(
    questions.map((p) => [p.questionId, p.statement])
  );

  return (
    <View className="box p-6 rounded-2xl border border-border flex flex-col gap-5 max-w-2xl">
      <View className="flex flex-col items-center gap-3 text-center">
        <View
          className={`flex items-center justify-center w-16 h-16 rounded-full ${
            passed
              ? "bg-green-100 text-green-600 dark:bg-green-900/40 dark:text-green-400"
              : "bg-destructive/10 text-destructive"
          }`}
        >
          <Icon
            name={
              passed ? "fa-solid fa-circle-check" : "fa-solid fa-circle-xmark"
            }
            className="text-3xl"
          />
        </View>
        <Text type="h2" className="text-xl font-bold text-foreground">
          {passed ? t("resultApproved") : t("resultFailed")}
        </Text>
        <Text className="text-sm text-muted-foreground">
          {`${t("correctAnswers")}: ${result.correct ?? 0} / ${result.total}`}
          {` · ${t("minToPass")}: ${result.minCorrect}`}
        </Text>
      </View>

      {result.feedback && result.feedback.length > 0 && (
        <View className="flex flex-col gap-2">
          <Text type="h3" className="text-sm font-semibold text-foreground">
            {t("reviewByQuestion")}
          </Text>
          <View type="ul" className="flex flex-col gap-2">
            {result.feedback.map((item, index) => (
              <View
                key={item.questionId}
                type="li"
                className="flex items-start gap-3 p-3 rounded-xl bg-muted"
              >
                <Icon
                  name={
                    item.isCorrect
                      ? "fa-solid fa-circle-check"
                      : "fa-solid fa-circle-xmark"
                  }
                  className={`mt-0.5 shrink-0 ${
                    item.isCorrect
                      ? "text-green-600 dark:text-green-400"
                      : "text-destructive"
                  }`}
                />
                <View className="flex flex-col gap-1 min-w-0">
                  <Text className="text-sm font-medium text-foreground">
                    {enunciadoBy.get(item.questionId) ??
                      `${t("question")} ${index + 1}`}
                  </Text>
                  {!item.isCorrect && item.explanation && (
                    <Text className="text-xs text-muted-foreground">
                      {item.explanation}
                    </Text>
                  )}
                </View>
              </View>
            ))}
          </View>
        </View>
      )}

      {!passed && result.topicsToReview && result.topicsToReview.length > 0 && (
        <View className="flex flex-col gap-1">
          <Text type="h3" className="text-sm font-semibold text-foreground">
            {t("topicsToReinforce")}
          </Text>
          <Text className="text-sm text-muted-foreground">
            {result.topicsToReview.join(" · ")}
          </Text>
        </View>
      )}

      {children && <View className="flex justify-center pt-2">{children}</View>}
    </View>
  );
};

export default ResultCard;
